import { useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { handleCloseSurveyOnDialog } from "../services/Hotjar/handleCloseSurveyOnDialog";
import { handleCloseSurveyOnCornerButton } from "../services/Hotjar/handleCloseSurveyOnCornerButton";

/**
 * Module-level flag to prevent double open
 */
let hasOpenedSurvey = false;

export function useHotjarSurveys() {
  const navigate = useNavigate();
  const location = useLocation();

  const setRunHotjarSurvey = useCallback(
    ({ key = "feedback", surveyName }) => {
      const params = new URLSearchParams(location.search);

      if (surveyName) {
        params.set(key, surveyName);
      } else {
        params.set(key, "true");
      }

      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        { replace: true }
      );
    },
    [navigate, location]
  );

  /**
   * OPEN SURVEY
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const surveyParam = params.get("feedback");

    if (!surveyParam || hasOpenedSurvey) return;

    const observer = new MutationObserver(() => {
      const button = document.querySelector(
        "button[aria-label*='Feedback'], button[id^='hj-survey-toggle-']"
      );

      if (button && !hasOpenedSurvey) {
        hasOpenedSurvey = true;

        observer.disconnect();

        // slight async prevents toggle glitch
        setTimeout(() => {
          button.click();
        }, 1300);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [location.search]);

  /**
   * REMOVE PARAM ONLY AFTER USER FINISHES SURVEY
   * Anchored by confirmation heading text
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const surveyParam = params.get("feedback");

    if (!surveyParam) return;

    const observer = new MutationObserver(() => {
      const foundCloseButton =
        handleCloseSurveyOnCornerButton() || handleCloseSurveyOnDialog();

      if (!foundCloseButton) return;

      const handleClose = () => {
        const newParams = new URLSearchParams(location.search);
        newParams.delete("feedback");

        navigate(
          {
            pathname: location.pathname,
            search: newParams.toString() ? `?${newParams.toString()}` : "",
          },
          { replace: true }
        );

        hasOpenedSurvey = false;
      };

      foundCloseButton.addEventListener("click", handleClose, { once: true });

      observer.disconnect();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [location.search, navigate, location.pathname]);

  return {
    setRunHotjarSurvey,
  };
}
